import '../styles/Notes.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { auth } from "../config/firebase";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from "primereact/button";
import { confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import {InputText} from "primereact/inputtext";

const Notes = () => {
    const [notes, setNotes] = useState([]);
    const [selectedNote, setSelectedNote] = useState(null);
    const [editorContent, setEditorContent] = useState('');
    const [noteTitle, setNoteTitle] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showMiniMenu, setShowMiniMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [user, setUser] = useState(null);
    const [lastSavedContent, setLastSavedContent] = useState('');
    const [lastSavedTitle, setLastSavedTitle] = useState('');
    const quillRef = useRef(null);
    const unsubscribersRef = useRef([]);
    const saveCurrentNoteRef = useRef(null);

    // Function to check if there are unsaved changes
    const hasUnsavedChanges = useCallback(() => {
        return editorContent !== lastSavedContent || noteTitle !== lastSavedTitle;
    }, [editorContent, noteTitle, lastSavedContent, lastSavedTitle]);

    // Function to prompt user to save changes before navigating away
    const promptSaveBeforeNavigating = useCallback((callback) => {
        if (hasUnsavedChanges()) {
            confirmDialog({
                message: 'You have unsaved changes. Do you want to save them before leaving?',
                header: 'Unsaved Changes',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Save',
                rejectLabel: 'Discard',
                accept: async () => {
                    try {
                        // Use the ref to access the latest version of saveCurrentNote
                        if (saveCurrentNoteRef.current) {
                            await saveCurrentNoteRef.current();
                        }
                        if (callback) callback();
                    } catch (error) {
                        console.error("Error saving note before navigation:", error);
                        toast.error("Failed to save note");
                    }
                },
                reject: () => {
                    if (callback) callback();
                }
            });
        } else {
            if (callback) callback();
        }
    }, [hasUnsavedChanges]); // Removed saveCurrentNote from dependencies

    useEffect(() => {
        if (notes.length > 0 && !selectedNote) {
            setSelectedNote(notes[0]);
            setNoteTitle(notes[0].title || '');
            setEditorContent(notes[0].content || '');
            setLastSavedContent(notes[0].content || '');
            setLastSavedTitle(notes[0].title || '');
        }
    }, [notes, selectedNote]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                setupRealTimeListeners(currentUser.uid);
            } else {
                // Clean up listeners
                unsubscribersRef.current.forEach(unsub => unsub());
                unsubscribersRef.current = [];

                setNotes([]);
                setSelectedNote(null);
                setLoading(false);
            }
        });

        return () => {
            unsubscribe();
            // Clean up all active listeners
            unsubscribersRef.current.forEach(unsub => unsub());
        };
    }, []);


    const setupRealTimeListeners = (userId) => {
        setLoading(true);
        const db = getFirestore();

        try {
            // Clean up any existing listeners
            unsubscribersRef.current.forEach(unsub => unsub());
            unsubscribersRef.current = [];

            console.log("Setting up Firestore listeners for notes with userId:", userId);

            // Set up listener for current user's notes
            const userNotesListener = onSnapshot(
                doc(db, "Notes", userId),
                (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const userData = docSnapshot.data();
                        const userNotes = userData.notes || [];
                        console.log("User notes updated:", userNotes.length);

                        // Store user notes in state, preserving partner notes
                        setNotes(prevNotes => {
                            // Filter out old user notes, keep only partner notes
                            const partnerNotes = prevNotes.filter(note => note.creator?.id !== userId);
                            return [...userNotes, ...partnerNotes];
                        });
                    } else {
                        console.log("User notes document does not exist");
                        // If the document doesn't exist, just keep any partner notes
                        setNotes(prevNotes => {
                            return prevNotes.filter(note => note.creator?.id !== userId);
                        });
                    }
                    setLoading(false);
                },
                (error) => {
                    console.error("Error in user notes listener:", error);
                    toast.error("Failed to load notes");
                    setLoading(false);
                }
            );
            unsubscribersRef.current.push(userNotesListener);

            // Get partner ID from UserInformation collection
            axios.get(`https://api.frazza.online/user/${userId}/relationship`)
                .then(response => {
                    if (response.data.success && response.data.data.inRelation) {
                        const partnerId = response.data.data.inRelationWith;
                        console.log("Setting up partner notes listener with partnerId:", partnerId);

                        // Set up listener for partner's notes
                        const partnerNotesListener = onSnapshot(
                            doc(db, "Notes", partnerId),
                            (docSnapshot) => {
                                if (docSnapshot.exists()) {
                                    const partnerData = docSnapshot.data();
                                    const partnerNotes = partnerData.notes || [];
                                    console.log("Partner notes updated:", partnerNotes.length);

                                    setNotes(prevNotes => {
                                        // Filter out old partner notes, keep only user notes
                                        const userNotes = prevNotes.filter(note => note.creator?.id === userId);

                                        // Merge user notes with updated partner notes
                                        const mergedNotes = [...userNotes, ...partnerNotes];

                                        // If a note is currently selected, also update it if it changed
                                        if (selectedNote && selectedNote.creator?.id !== userId) {
                                            const updatedSelectedNote = partnerNotes.find(note => note.id === selectedNote.id);
                                            if (updatedSelectedNote) {
                                                console.log("Selected partner note was updated externally");
                                                // We'll handle UI updates in the useEffect
                                            }
                                        }

                                        return mergedNotes;
                                    });
                                } else {
                                    console.log("Partner notes document does not exist");
                                }
                            },
                            (error) => {
                                console.error("Error in partner notes listener:", error);
                            }
                        );
                        unsubscribersRef.current.push(partnerNotesListener);
                    }
                })
                .catch(error => {
                    console.error("Error fetching relationship:", error);
                });

        } catch (error) {
            console.error("Error setting up real-time listeners:", error);
            toast.error("Failed to load notes");
            setLoading(false);
        }
    };



    const saveCurrentNote = async () => {
        if (!selectedNote) return Promise.resolve(false);

        // If content hasn't changed, don't save
        if (editorContent === lastSavedContent && noteTitle === lastSavedTitle) {
            return Promise.resolve(false);
        }

        const currentUser = auth.currentUser;
        if (!currentUser) return Promise.reject("No user logged in");

        // Determine if this is the user's note or partner's note
        const isPartnerNote = selectedNote.creator?.id !== currentUser.uid;

        try {
            setSaving(true);

            const updatedNote = {
                ...selectedNote,
                content: editorContent,
                title: noteTitle,
                updatedAt: new Date().toISOString()
            };

            // Use the appropriate user ID based on whether it's a partner note
            const userId = isPartnerNote ? selectedNote.creator.id : currentUser.uid;

            // Save the note using the REST API
            await axios.post('https://api.frazza.online/notes', {
                userId,
                note: updatedNote
            });

            setLastSavedContent(editorContent);
            setLastSavedTitle(noteTitle);

            return Promise.resolve(true);
        } catch (error) {
            console.error("Error saving note:", error);
            toast.error("Failed to save note");
            return Promise.reject(error);
        } finally {
            setSaving(false);
        }
    };

    // Update the ref whenever saveCurrentNote changes
    useEffect(() => {
        saveCurrentNoteRef.current = saveCurrentNote;
    }, [saveCurrentNote]);

    // Handle keyboard shortcuts for saving
    useEffect(() => {
        const handleKeyDown = async (e) => {
            // Check for Command+S (Mac) or Control+S (Windows)
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault(); // Prevent browser save dialog
                try {
                    const saved = await saveCurrentNote();
                    if (saved) {
                        toast.success("Note saved successfully");
                    }
                } catch (error) {
                    console.error("Error saving note with keyboard shortcut:", error);
                    toast.error("Failed to save note");
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [editorContent, noteTitle, selectedNote, lastSavedContent, lastSavedTitle]);

    useEffect(() => {
        if (!quillRef.current) return;

        const handleKeyUp = (event) => {
            if (event.key === '.') {
                const selection = quillRef.current.getEditor().getSelection();
                if (selection) {
                    const bounds = quillRef.current.getEditor().getBounds(selection.index);
                    if (bounds) {
                        const editorRect = quillRef.current.getEditor().root.getBoundingClientRect();
                        setMenuPosition({
                            top: bounds.top + bounds.height + editorRect.top,
                            left: bounds.left + editorRect.left
                        });
                        setShowMiniMenu(true);
                    }
                }
            } else if (event.key === 'Backspace' || event.key === 'Delete') {
                // Hide mini menu when backspace or delete is pressed
                setShowMiniMenu(false);
            }
        };


        // Also listen for text changes to detect when the "." is deleted
        const handleTextChange = () => {
            if (showMiniMenu) {
                const selection = quillRef.current.getEditor().getSelection();
                if (selection) {
                    // Get the character before the cursor
                    const text = quillRef.current.getEditor().getText(selection.index - 1, 1);
                    // If it's not a period, hide the menu
                    if (text !== '.') {
                        setShowMiniMenu(false);
                    }
                }
            }
        };

        const editorElement = quillRef.current.getEditor().root;
        editorElement.addEventListener('keyup', handleKeyUp);

        // Add text-change event to the editor
        const editor = quillRef.current.getEditor();
        editor.on('text-change', handleTextChange);

        return () => {
            editorElement.removeEventListener('keyup', handleKeyUp);
            editor.off('text-change', handleTextChange);
        };
    }, [quillRef.current, selectedNote, showMiniMenu]);

    // Close mini menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showMiniMenu && !event.target.closest('.mini-menu')) {
                setShowMiniMenu(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showMiniMenu]);

    // Add this effect after your other useEffect hooks
// Modify this useEffect to track notes and selected note changes properly
    useEffect(() => {
        if (selectedNote) {
            // Find the current version of this note
            const updatedNote = notes.find(note => note.id === selectedNote.id);

            if (updatedNote) {
                // Only update if we don't have unsaved changes
                const hasUnsavedChangesLocal =
                    editorContent !== lastSavedContent ||
                    noteTitle !== lastSavedTitle;

                const noteWasChangedExternally =
                    updatedNote.content !== lastSavedContent ||
                    updatedNote.title !== lastSavedTitle;

                if (!hasUnsavedChangesLocal && noteWasChangedExternally) {
                    console.log("Updating editor with real-time changes", updatedNote.id);
                    setEditorContent(updatedNote.content || '');
                    setNoteTitle(updatedNote.title || '');
                    setLastSavedContent(updatedNote.content || '');
                    setLastSavedTitle(updatedNote.title || '');
                }
            }
        }
    }, [notes, selectedNote, lastSavedContent, lastSavedTitle]);

    // Handle browser navigation/refresh warning
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges()) {
                // Standard way to show a confirmation dialog when leaving the page
                e.preventDefault();
                const message = "You have unsaved changes. Are you sure you want to leave?";
                e.returnValue = message; // For older browsers
                return message; // For modern browsers
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    // Handle component unmounting warning
    useEffect(() => {
        return () => {
            // This runs when the component is about to unmount
            if (hasUnsavedChanges()) {
                // We can't prevent navigation at this point, but we can show a toast
                // to inform the user that they have unsaved changes
                toast.warning("You have unsaved changes that weren't saved!");
            }
        };
    }, [hasUnsavedChanges]);

    // Quill editor modules configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['link'], // Removed 'image'
            [{ 'table': true }],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'align',
        'list', 'bullet', 'indent',
        'link', // Removed 'image'
        'table',
        'clean'
    ];

    const addNewNote = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            return toast.error("You must be logged in to create notes");
        }

        // Save current note before creating a new one
        if (selectedNote && (editorContent !== lastSavedContent || noteTitle !== lastSavedTitle)) {
            try {
                await saveCurrentNote();
            } catch (error) {
                toast.error("Failed to save current note before creating new one");
                return;
            }
        }

        const newNote = {
            id: Date.now(),
            title: 'New Note',
            content: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            creator: {
                id: currentUser.uid,
                name: user ? user.displayName || "You" : "You"
            }
        };

        try {
            // Save the new note using the REST API
            await axios.post('https://api.frazza.online/notes', {
                userId: currentUser.uid,
                note: newNote
            });

            // Update local state
            setSelectedNote(newNote);
            setNoteTitle('New Note');
            setEditorContent('');
            setLastSavedContent('');
            setLastSavedTitle('New Note');
        } catch (error) {
            console.error("Error creating new note:", error);
            toast.error("Failed to save new note");
        }
    };

    const handleEditorChange = (content) => {
        setEditorContent(content);
    };

    const handleSelectNote = async (note) => {
        // Don't do anything if clicking on the already selected note or during saving
        if (selectedNote?.id === note.id || saving) {
            return;
        }


        if (selectedNote && (editorContent !== lastSavedContent || noteTitle !== lastSavedTitle)) {
            try {
                await saveCurrentNote();
            } catch (error) {
                console.error("Failed to save note before switching:", error);
                toast.error("Failed to save current note");
                return; // Don't proceed if save fails
            }
        }

        // Switch to the selected note
        setSelectedNote(note);
        setNoteTitle(note.title || '');
        setEditorContent(note.content || '');
        setLastSavedContent(note.content || '');
        setLastSavedTitle(note.title || '');
    };

    const deleteNote = async () => {
        if (!selectedNote || saving) return;

        const currentUser = auth.currentUser;
        if (!currentUser) return toast.error("You must be logged in to delete notes");

        // Check if this is a partner's note
        const isPartnerNote = selectedNote.creator?.id !== currentUser.uid;

        if (isPartnerNote) {
            return toast.error("You cannot delete your partner's notes");
        }

        try {
            setSaving(true);

            // Delete the note using the REST API
            await axios.delete(`https://api.frazza.online/notes/${selectedNote.id}`, {
                params: { userId: currentUser.uid }
            });

            // Update local state
            setNotes(prevNotes => prevNotes.filter(note => note.id !== selectedNote.id));
            setSelectedNote(null);
            setNoteTitle('');
            setEditorContent('');
            setLastSavedContent('');
            setLastSavedTitle('');
            toast.success("Note deleted successfully");
        } catch (error) {
            console.error("Error deleting note:", error);
            toast.error("Failed to delete note");
        } finally {
            setSaving(false);
        }
    };

    const handleTitleChange = (e) => {
        setNoteTitle(e.target.value);
    };

    const handleMiniMenuSelection = (type) => {
        if (!quillRef.current) return;

        const editor = quillRef.current.getEditor();
        const selection = editor.getSelection();

        if (selection) {
            // Delete the dot that triggered the menu
            editor.deleteText(selection.index - 1, 1);

            if (type === 'todo') {
                editor.insertText(selection.index - 1, ' ');
                editor.insertEmbed(selection.index - 1, 'formula', '☐ ');
                editor.insertText(selection.index, ' Todo item');
            } else if (type === 'event') {
                // Insert event
                editor.insertText(selection.index - 1, ' ');
                editor.insertEmbed(selection.index - 1, 'formula', '📅 ');
                editor.insertText(selection.index, ' Event: ');
            }

            // Move cursor to the end of the inserted content
            editor.setSelection(selection.index + 10, 0);
        }

        setShowMiniMenu(false);
    };

    const filteredNotes = notes.filter(note =>
        note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.content?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="notes-container">
            <div className="notes-sidebar">
                <div className="notes-sidebar-header">
                    <div className="search-box-notes">
                        <i className="pi pi-search search-iconNotes"/>
                        <InputText
                            placeholder="Search notes"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="main-search"
                        />
                    </div>
                </div>


                <div className="notes-list">
                    {loading ? (
                        <div className="loading-notes">Loading notes...</div>
                    ) : filteredNotes.length === 0 ? (
                        <div className="empty-notes">No notes found</div>
                    ) : (
                        filteredNotes.map(note => (
                            <div
                                key={note.id}
                                className={`note-item ${selectedNote?.id === note.id ? 'selected' : ''}`}
                                onClick={() => handleSelectNote(note)}
                            >
                                <div className="note-title">{note.title || 'Untitled'}</div>
                                <div className="note-preview">
                                    {note.content?.replace(/<[^>]*>?/gm, '').substring(0, 50) || ''}
                                    {note.content?.length > 50 ? '...' : ''}
                                </div>
                                <div className="note-meta">
                                    {note.creator?.name && (
                                        <div className="note-creator">By: {note.creator.name}</div>
                                    )}
                                    <div className="note-date">
                                        {new Date(note.updatedAt).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="notes-editor">
                <Button
                    onClick={addNewNote}
                    className="add-note-btn"
                    disabled={saving}
                >
                    <i className="pi pi-plus"></i>
                </Button>
                {saving && (
                    <div className="saving-indicator" style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        zIndex: 9999
                    }}>
                        Saving...
                    </div>
                )}
                {selectedNote ? (
                    <>
                        <div className="editor-header">
                            <input
                                type="text"
                                value={noteTitle}
                                onChange={handleTitleChange}
                                className="note-title-input"
                                placeholder="Note title"
                                disabled={saving}
                            />
                            <div className="editor-actions">
                                <Button
                                    onClick={async () => {
                                        try {
                                            const saved = await saveCurrentNote();
                                            if (saved) {
                                                toast.success("Note saved successfully");
                                            }
                                        } catch (error) {
                                            console.error("Error saving note:", error);
                                            toast.error("Failed to save note");
                                        }
                                    }}
                                    className="save-note-btn"
                                    disabled={saving}
                                >
                                    <i className="pi pi-save"></i> Save
                                </Button>
                                <Button
                                    onClick={deleteNote}
                                    className="delete-note-btn"
                                    disabled={saving || (selectedNote?.creator?.id !== auth.currentUser?.uid)}
                                >
                                    <i className="pi pi-trash"></i> Delete
                                </Button>
                            </div>
                        </div>
                        <div className="editor-container">
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={editorContent}
                                onChange={handleEditorChange}
                                modules={modules}
                                formats={formats}
                                className="quill-editor"
                                readOnly={saving}
                            />
                            {showMiniMenu && (
                                <div
                                    className="mini-menu"
                                    style={{
                                        position: 'absolute',
                                        top: `${menuPosition.top}px`,
                                        left: `${menuPosition.left}px`,
                                        zIndex: 1000,
                                        background: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        padding: '5px 0'
                                    }}
                                >
                                    <div
                                        className="menu-item"
                                        onClick={() => handleMiniMenuSelection('todo')}
                                        style={{
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            hover: {backgroundColor: '#f5f5f5'}
                                        }}
                                    >
                                        <i className="pi pi-check-square" style={{marginRight: '8px'}}></i>
                                        Create Todo
                                    </div>
                                    <div
                                        className="menu-item"
                                        onClick={() => handleMiniMenuSelection('event')}
                                        style={{
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            hover: {backgroundColor: '#f5f5f5'}
                                        }}
                                    >
                                        <i className="pi pi-calendar" style={{marginRight: '8px'}}></i>
                                        Create Event
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="no-note-selected">
                        <div className="empty-state">
                            <i className="pi pi-file-edit" style={{ fontSize: '3rem' }}></i>
                            <h2>No Note Selected</h2>
                            <p>Select a note from the sidebar or create a new one</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Notes;
